// Shapes a flat submission list into the nesting the "View Submissions" screen
// renders: one entry per submitter, each holding their original submissions
// with every re-submission tucked underneath the one it replaced. Devs get one
// more level on top, since their roster spans companies.

const timeOf = (submission) => {
  const ms = new Date(submission?.submitted_at).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

const byNewestFirst = (a, b) => timeOf(b) - timeOf(a);

// A re-submission points at the submission it replaced, which may itself be a
// re-submission, so walk the chain up to the original. A parent that isn't in
// the list (scoping hid it, or it was deleted) makes the row its own root
// rather than dropping it — an orphan is still a submission worth showing.
const rootIdOf = (submission, byId) => {
  const seen = new Set();
  let current = submission;
  while (current.parent_submission_id != null && !seen.has(current.id)) {
    seen.add(current.id);
    const parent = byId.get(current.parent_submission_id);
    if (!parent) break;
    current = parent;
  }
  return current.id;
};

export const groupSubmissionsByUser = (submissions = []) => {
  const byId = new Map(submissions.map((s) => [s.id, s]));

  // Collect each original submission together with its re-submissions.
  const chains = new Map();
  submissions.forEach((submission) => {
    const rootId = rootIdOf(submission, byId);
    if (!chains.has(rootId)) {
      chains.set(rootId, { root: byId.get(rootId) || submission, resubmissions: [] });
    }
    if (submission.id !== rootId) chains.get(rootId).resubmissions.push(submission);
  });

  const users = new Map();
  chains.forEach((chain) => {
    chain.resubmissions.sort(byNewestFirst);
    // Ordering follows the original's date — the one the row displays — so the
    // list reads top-to-bottom newest-first. ``latestAt`` is the freshest date
    // in the chain, shown alongside it when a re-submission is more recent.
    const startedAt = timeOf(chain.root);
    const latestAt = Math.max(startedAt, ...chain.resubmissions.map(timeOf));

    const key = chain.root.user_id ?? chain.root.user_email ?? 'unknown';
    if (!users.has(key)) {
      users.set(key, {
        key,
        userId: chain.root.user_id ?? null,
        email: chain.root.user_email || 'Unknown submitter',
        companyId: chain.root.company_id ?? null,
        companyName: chain.root.company_name || null,
        submissionCount: 0,
        startedAt: 0,
        chains: [],
      });
    }
    const entry = users.get(key);
    entry.chains.push({ ...chain, startedAt, latestAt });
    entry.submissionCount += 1 + chain.resubmissions.length;
    entry.startedAt = Math.max(entry.startedAt, startedAt);
  });

  const result = Array.from(users.values());
  result.forEach((entry) => entry.chains.sort((a, b) => b.startedAt - a.startedAt));
  result.sort((a, b) => b.startedAt - a.startedAt);
  return result;
};

// Devs see submissions from every company at once, so their view adds a company
// level above the submitters. Companies sort by name — a stable, scannable
// order — while submitters inside one keep their newest-first ordering.
export const groupSubmissionsByCompany = (submissions = []) => {
  const companies = new Map();

  groupSubmissionsByUser(submissions).forEach((user) => {
    const key = user.companyId ?? 'none';
    if (!companies.has(key)) {
      companies.set(key, {
        key: `company-${key}`,
        companyId: user.companyId,
        companyName: user.companyName || 'No company',
        users: [],
        submissionCount: 0,
      });
    }
    const company = companies.get(key);
    company.users.push(user);
    company.submissionCount += user.submissionCount;
  });

  return Array.from(companies.values()).sort((a, b) =>
    a.companyName.localeCompare(b.companyName)
  );
};
